// Recreates the look defined in src/styles/mapStyles.json (Apple MapKit format)
// on top of a MapLibre vector style (OpenMapTiles / OpenFreeMap schema).
const COLOR = {
  base:        "#f2f2f2",  // natural.base — background between everything else
  land:        "#ffffff",  // natural (parks, wood, generic landcover)
  water:       "#c8d7d4",  // natural.water
  waterLabel:  "#070707",
  roadFill:    "#eeeeee",  // infrastructure.roadNetwork geometry.fillColor @ 0.5 opacity
  roadCasing:  "#ff6b6b",  // infrastructure.roadNetwork geometry.strokeColor
  label:       "#7b7b7b",  // infrastructure + political textFillColor
  halo:        "#ffffff",
};

const ROAD_FILL_OPACITY = 0.5;

const LAND_SOURCE_LAYERS = new Set([
  "landcover", "landuse", "park", "wood", "natural", "national_park",
]);

// Wetlands (swamps, marshes, bogs, fens, mangroves) are styled identically to
// open water — same fill color, no hatching pattern, full opacity.
const WETLAND_PATTERN = /wetland|swamp|marsh|bog|fen|mangrove/i;

const isHidden = (id) =>
  id.includes("poi") ||
  id.includes("transit") ||
  id.includes("rail") ||
  id.includes("ferry") ||
  id.includes("aerialway") ||
  id.includes("aeroway");

const set = (map, layerId, prop, value, kind) => {
  try {
    if (kind === "paint") map.setPaintProperty(layerId, prop, value);
    else map.setLayoutProperty(layerId, prop, value);
  } catch { /* layer doesn't support this property — ignore */ }
};

export function applyMapStyleTransform(map) {
  const style = map.getStyle();
  if (!style?.layers) return;

  for (const layer of style.layers) {
    const { id, type } = layer;
    const sourceLayer = layer["source-layer"];

    if (isHidden(id)) {
      set(map, id, "visibility", "none", "layout");
      continue;
    }

    if (type === "background") {
      set(map, id, "background-color", COLOR.base, "paint");
      continue;
    }

    if ((sourceLayer === "water" && type === "fill") ||
        (WETLAND_PATTERN.test(id) && type === "fill")) {
      set(map, id, "fill-color", COLOR.water, "paint");
      set(map, id, "fill-pattern", null, "paint");
      set(map, id, "fill-opacity", 1, "paint");
      continue;
    }

    if (sourceLayer === "water_name" && type === "symbol") {
      set(map, id, "text-color", COLOR.waterLabel, "paint");
      set(map, id, "text-halo-color", COLOR.halo, "paint");
      continue;
    }

    if (LAND_SOURCE_LAYERS.has(sourceLayer) && type === "fill") {
      set(map, id, "fill-color", COLOR.land, "paint");
      continue;
    }

    if (sourceLayer === "transportation" && type === "line") {
      // OMT splits each road class into a casing layer (drawn first, wider)
      // and a fill layer (drawn on top, narrower). Detect by id suffix.
      const isCasing = /casing|outline/i.test(id);
      if (isCasing) {
        set(map, id, "line-color", COLOR.roadCasing, "paint");
        set(map, id, "line-opacity", 1, "paint");
      } else {
        set(map, id, "line-color", COLOR.roadFill, "paint");
        set(map, id, "line-opacity", ROAD_FILL_OPACITY, "paint");
      }
      continue;
    }

    if (sourceLayer === "transportation_name" && type === "symbol") {
      set(map, id, "text-color", COLOR.label, "paint");
      set(map, id, "text-halo-color", COLOR.halo, "paint");
      continue;
    }

    if (sourceLayer === "place" && type === "symbol") {
      set(map, id, "text-color", COLOR.label, "paint");
      set(map, id, "text-halo-color", COLOR.halo, "paint");
      continue;
    }

    if (sourceLayer === "boundary" && type === "line") {
      set(map, id, "line-color", COLOR.label, "paint");
      continue;
    }
  }
}
